/* ==========================================================================
   B97 Orbit — collection.js
   Filtros y orden nativos (Storefront Filtering) sin recargar la página:
   se vuelve a pedir la sección con la Section Rendering API.
   Sin JavaScript, todo funciona con enlaces y el botón «Aplicar».
   ========================================================================== */
(() => {
  'use strict';

  const B97 = window.B97 || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const debounce = B97.debounce || ((fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; });
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const DENSITY_KEY = 'b97-grid-density';

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.cache = new Map();
      this.form = this.querySelector('[data-facets-form]');

      if (this.form) {
        const onChange = debounce(() => this.submitForm(), 420);
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('[data-price-input]')) return;
          this.submitForm();
        });
        this.form.addEventListener('input', (event) => {
          if (event.target.matches('[data-price-input]')) onChange();
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submitForm();
        });
      }

      const proxy = this.querySelector('[data-sort-proxy]');
      if (proxy) {
        proxy.addEventListener('change', () => {
          const real = this.querySelector('[data-sort-select]');
          if (real) {
            real.value = proxy.value;
            this.submitForm();
          } else {
            const url = new URL(window.location.href);
            url.searchParams.set('sort_by', proxy.value);
            url.searchParams.delete('page');
            this.render(url.searchParams.toString());
          }
        });
      }

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.render(new URL(link.href, window.location.origin).searchParams.toString());
          return;
        }
        const more = event.target.closest('[data-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
          return;
        }
        const densityBtn = event.target.closest('button[data-density]');
        if (densityBtn) this.setDensity(densityBtn.dataset.density, true);
      });

      window.addEventListener('popstate', () => {
        if (this.isConnected) this.render(window.location.search.slice(1), false);
      });

      let saved = null;
      try { saved = localStorage.getItem(DENSITY_KEY); } catch (e) { saved = null; }
      if (saved) this.setDensity(saved, false);
    }

    queryFromForm() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value !== '') params.append(key, value);
      }
      return params.toString();
    }

    submitForm() {
      this.render(this.queryFromForm());
    }

    async fetchSection(query) {
      const base = this.dataset.baseUrl || window.location.pathname;
      const url = `${base}?section_id=${encodeURIComponent(this.sectionId)}${query ? `&${query}` : ''}`;
      if (this.cache.has(url)) return this.cache.get(url);
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      this.cache.set(url, html);
      return html;
    }

    async render(query, push = true) {
      if (this.controller) this.controller.abort();
      this.classList.add('is-loading');
      try {
        const html = await this.fetchSection(query);
        const doc = parse(html);
        this.swap(doc, '[data-results]');
        this.swap(doc, '[data-active-filters]');
        this.swap(doc, '[data-product-count]');
        this.swapGroups(doc);
        this.syncCounts(doc);
        if (push) {
          const base = this.dataset.baseUrl || window.location.pathname;
          window.history.pushState({}, '', `${base}${query ? `?${query}` : ''}`);
        }
        const bar = this.querySelector('[data-catalog-bar]');
        if (bar && push && bar.getBoundingClientRect().top < 0) {
          bar.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
        const density = this.getAttribute('data-density');
        if (density) this.setDensity(density, false);
      } catch (error) {
        if (B97.toast) B97.toast(error.message);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    swap(doc, selector) {
      const fresh = doc.querySelector(selector);
      const current = this.querySelector(selector);
      if (fresh && current) current.replaceWith(fresh);
    }

    /* Se sustituyen solo los grupos (recuentos y estados) para no cerrar
       el panel ni perder el foco del campo que se está usando. */
    swapGroups(doc) {
      const fresh = doc.querySelector('[data-facets-groups]');
      const current = this.querySelector('[data-facets-groups]');
      if (!fresh || !current) return;
      const openState = Array.from(current.querySelectorAll('details')).map((d) => d.open);
      const activeId = document.activeElement && document.activeElement.id;
      current.replaceWith(fresh);
      fresh.querySelectorAll('details').forEach((d, i) => {
        if (typeof openState[i] === 'boolean') d.open = openState[i];
      });
      if (activeId) {
        const el = document.getElementById(activeId);
        if (el && fresh.contains(el)) el.focus({ preventScroll: true });
      }
      const proxy = this.querySelector('[data-sort-proxy]');
      const real = fresh.querySelector('[data-sort-select]');
      if (proxy && real) proxy.value = real.value;
    }

    syncCounts(doc) {
      const freshBtn = doc.querySelector('[data-filter-count]');
      const btn = this.querySelector('[data-filter-count]');
      if (freshBtn && btn) {
        btn.textContent = freshBtn.textContent;
        btn.hidden = freshBtn.hidden;
      }
      const freshCount = doc.querySelector('[data-drawer-count]');
      const count = this.querySelector('[data-drawer-count]');
      if (freshCount && count) count.textContent = freshCount.textContent;
    }

    async loadMore(link) {
      if (link.classList.contains('is-loading')) return;
      link.classList.add('is-loading');
      try {
        const url = new URL(link.href, window.location.origin);
        const html = await this.fetchSection(url.searchParams.toString());
        const doc = parse(html);
        const freshGrid = doc.querySelector('[data-product-grid]');
        const grid = this.querySelector('[data-product-grid]');
        if (freshGrid && grid) {
          const firstNew = freshGrid.firstElementChild;
          grid.append(...Array.from(freshGrid.children));
          if (firstNew) {
            const focusable = firstNew.querySelector('a[href]');
            if (focusable) focusable.focus({ preventScroll: true });
          }
        }
        const freshPagination = doc.querySelector('[data-pagination]');
        const pagination = this.querySelector('[data-pagination]');
        if (pagination) {
          if (freshPagination) pagination.replaceWith(freshPagination);
          else pagination.remove();
        }
      } catch (error) {
        window.location.href = link.href;
      } finally {
        link.classList.remove('is-loading');
      }
    }

    setDensity(value, remember) {
      const grid = this.querySelector('[data-grid]');
      this.setAttribute('data-density', value);
      if (grid) {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        grid.setAttribute('data-cols', mobile ? (value === '2' ? '1' : '2') : value === '2' ? '2' : '4');
      }
      this.querySelectorAll('[data-density]').forEach((btn) => {
        if (btn.tagName === 'BUTTON') btn.setAttribute('aria-pressed', String(btn.dataset.density === value));
      });
      if (remember) {
        try { localStorage.setItem(DENSITY_KEY, value); } catch (e) { /* sin almacenamiento */ }
      }
    }
  }
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
